/*
 * Project: OKIT.VCM
 *
 * Copyright 2023 by OKIT GmbH
 * All rights reserved.
 *
 * Diese Software ist urheberrechtlich geschützt.
 */
import L from "leaflet";
import { getNameSpaceListApi } from "../../hooks/nameSpaceList";
import { getIntervallMeasurementApi } from "../../hooks/measurementApi";
import TimeOutToast from "../../utils/TimeOutToast";
import {
  iconstart,
  iconend,
  iconpause,
  iconTopSpeed,
} from "../../utils/iconStandard";
import i18next from "i18next";

/**
 * This component represents reset button to resset the screen zoom
 *
 * @author hatem sfar
 *
 */

/**
 *
 * @param {*} measurementDate
 * @returns
 */
function formatMeasurementDate(measurementDate) {
  const date = new Date(measurementDate);

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Note: Months are zero-based
  const year = date.getFullYear();

  return `${hours}:${minutes} ${day}.${month}.${year}`;
}

/**
 *
 * @param {*} res
 * @param {Array} measurements
 * @returns {Array} Updated measurements
 */
const processResponseData = (res, measurements) => {
  const measurementData = res.measurements.map((item) => ({
    id: item.id,
    content: item.content,
    contentType: item.contentType,
    measurementdate: item.measurementdate,
    node: res.namespace,
    device: res.device,
    latitude: "",
    longitude: "",
  }));
  return measurements.concat(measurementData);
};

/**
 *
 * @param {*} dateStart
 * @param {*} dateEnd
 * @param {*} selectedDevice
 * @param {*} nodeid
 * @param {*} mapRef
 * @param {*} allCoordinates
 * @param {*} measurements
 * @param {*} markerGroup
 */
const geteventdetails = async (
  dateStart,
  dateEnd,
  selectedDevice,
  nodeid,
  mapRef,
  allCoordinates,
  measurements,
  setEventForChart,
  setNodeForChart,
  setIsLoadingChart
) => {
  setNodeForChart(nodeid?.value);

  let isoDateStart = convertDateToISOWithOffset(dateStart);
  let isoDateEnd = dateEnd ? convertDateToISOWithOffset(dateEnd) : null;
  let pageNumber = 1;
  let nextPage = true;

  if (nodeid && mapRef.current) {
    mapRef.current.eachLayer((layer) => {
      if (layer instanceof L.Marker && layer.options.isDotChart) {
        mapRef.current.removeLayer(layer);
      }
    });
  }

  while (nextPage) {
    try {
      const response = await getIntervallMeasurementApi(
        selectedDevice?.value?.id,
        nodeid?.value?.namespace,
        isoDateStart,
        isoDateEnd,
        pageNumber
      );

      if (!response.data?.measurements) {
        nextPage = false;
        break;
      }

      const newMeasurements = response.data.measurements.flatMap((res) =>
        processResponseData(res, [])
      );

      measurements.push(...newMeasurements);

      // Ensure all coordinates are linked before adding markers
      await linkCoordinateToMeasurement(allCoordinates, measurements);

      nextPage = response.data.nextPage;
      pageNumber++;
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err?.response?.status === 504) TimeOutToast();
      nextPage = false;
    }
  }
  if (measurements?.length < 1) {
    setIsLoadingChart(false);
  }
  setEventForChart(measurements?.reverse());
};

const linkCoordinateToMeasurement = async (allCoordinates, measurements) => {
  const deviceCoordinates = allCoordinates;
  measurements.forEach((measurement) => {
    //console.log("measurement: ", measurement)

    if (deviceCoordinates) {
      //console.log("we have coordinate for selected device")
      // Find coordinates with matching device ID and closest measurement date
      let closestCoordinate = null;
      let closestDateDifference = Infinity;

      deviceCoordinates.forEach((coordinate) => {
        //console.log("coordinate: ", coordinate)
        const coordinateDate = new Date(coordinate.measurementdate);
        const measurementDate = new Date(measurement.measurementdate);
        const dateDifferenceInSeconds = Math.abs(
          (coordinateDate - measurementDate) / 1000
        );
        if (
          coordinate.device === measurement.device &&
          dateDifferenceInSeconds <= 60 && // Check if difference is within 60 seconds
          dateDifferenceInSeconds < closestDateDifference
        ) {
          closestCoordinate = coordinate;
          closestDateDifference = dateDifferenceInSeconds;
        }
      });

      if (closestCoordinate) {
        // Update latitude and longitude in the measurement
        measurement.latitude = closestCoordinate.latitude;
        measurement.longitude = closestCoordinate.longitude;
      }
    }
  });
};

/**
 *
 * @param {*} selectedDevice
 * @param {*} setNodes
 * @param {*} setNodeid
 */
const getNamespaceList = async (selectedDevice, setNodes, setNodeid) => {
  const response = await getNameSpaceListApi(selectedDevice.value.id);
  const sortedNamespaces = response?.sort((a, b) => {
    const labelA = a.node.namespace?.slice()?.toLowerCase();
    const labelB = b.node.namespace?.slice()?.toLowerCase();
    return labelA?.localeCompare(labelB);
  });
  const distinctNodes = Array.from(
    new Set(sortedNamespaces.map((node) => node.node.namespace))
  ).map((namespace) =>
    sortedNamespaces.find((node) => node.node.namespace === namespace)
  );
  const filteredNodes = distinctNodes.filter(
    (node) =>
      node.node.publicAvailable === true &&
      (node.node.contentType === "BOOL" ||
        node.node.contentType === "FLOAT" ||
        node.node.contentType === "DOUBLE")
  );
  //console.log("filteredNodes ",filteredNodes)
  setNodes(filteredNodes);
  setNodeid(null);
};

/**
 *
 * @param {*} coordinatesByDevice
 * @param {*} speedLimite
 * @param {*} LIMITE
 * @param {*} setAllCoordinates
 * @param {*} setPolylinesArray
 * @param {*} mapRef
 * @param {*} polylineGroup
 */
const addPolylines = (
  coordinatesByDevice,
  speedLimite,
  LIMITE,
  polylineGroup,
  setAllCoordinates,
  isColorReversed,
  isAnalytic,
  mapRef
) => {
  // Stop immediately if analytic mode is not active or map is unavailable
  if (!isAnalytic || !mapRef?.current) {
    return;
  }
  // Add the group of polylines and markers to the map
  const deviceCoordinates = coordinatesByDevice;
  setAllCoordinates(deviceCoordinates);

  // Set lat and long for each coordinate
  const latlngs = deviceCoordinates.map((coord) => [
    coord["latitude"],
    coord["longitude"],
  ]);

  // Options for the polyline
  const polylineOptions = {
    weight: 4,
    opacity: 0.5,
    isPolyline: true,
  };

  // Iterate through deviceCoordinates to set color based on speed
  for (let i = 0; i < deviceCoordinates.length - 1; i++) {

    const speed = deviceCoordinates[i]["speed"];
    const color = getSpeedColor(
      speed,
      speedLimite.value,
      LIMITE,
      isColorReversed
    );
    polylineOptions.color = color;

    // Create a polyline segment for this pair of coordinates with corresponding color
    const segmentLatLngs = [latlngs[i], latlngs[i + 1]];
    const polyline = L.polyline(segmentLatLngs, polylineOptions);

    // Bind tooltip with the measurement date and speed for this coordinate
    const measurementDate = formatMeasurementDateLine(
      deviceCoordinates[i]["measurementdate"]
    );
    const measurementSpeed = deviceCoordinates[i]["speed"] + " km/h";

    polyline.bindTooltip(measurementDate + ", " + measurementSpeed, {
      permanent: false,
      direction: "top",
    });

    polyline.bindPopup(`Device: ${deviceCoordinates[i]["deviceName"]}`);
      polylineGroup.addLayer(polyline);
    
  }
};

// Function to format measurement date (seconds, minutes, hours, and date)
const formatMeasurementDateLine = (timestamp) => {
  const date = new Date(timestamp);

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Note: Months are zero-based
  const year = date.getFullYear();

  return `${hours}:${minutes}:${seconds}, ${day}.${month}.${year}`;
};

/**
 *
 * @param {*} speed
 * @param {*} valueSpeedSelected
 * @param {*} extraRange
 * @returns
 */
const getSpeedColor = (
  speed,
  valueSpeedSelected,
  extraRange,
  isColorReversed
) => {
  if (valueSpeedSelected === 30) {
    extraRange = extraRange + 5;
  } else if (valueSpeedSelected === 40) {
    extraRange = extraRange + 10;
  }
  if (valueSpeedSelected === 75) {
    extraRange = extraRange + 25;
  }
  if (valueSpeedSelected === 110) {
    extraRange = extraRange + 50;
  }

  //console.log("extraRange: ", extraRange)

  if (isNaN(speed)) {
    return "black";
  } else if (!isNaN(speed) && speed > valueSpeedSelected) {
    return isColorReversed ? "red" : "green";
  } else if (!isNaN(speed) && speed < valueSpeedSelected - extraRange) {
    return isColorReversed ? "green" : "red";
  } else if (
    !isNaN(speed) &&
    valueSpeedSelected - extraRange <= speed <= valueSpeedSelected
  ) {
    return "blue";
  }
};

/**
 *
 * @param {*} deviceCoordinates
 * @param {*} mapRef
 * @param {*} pos
 */
const addMarkers = (
  deviceCoordinates,
  coordinateMeasurment,
  mapRef,
  pos,
  location,
  textPopUp,
  textTime,
  tourData
) => {
  // Stop immediately if map is unavailable
  if (!mapRef?.current) {
    return;
  }
  const lastCoordinate = deviceCoordinates;
  const marker = L.marker(
    [lastCoordinate["latitude"], lastCoordinate["longitude"]],
    {
      icon: createIcon(pos),
      isTripMarker: true,
      tripMarkerType: pos,
    }
  );
  const startedTime = formatMeasurementDate(
    coordinateMeasurment["measurementdate"]
  );
  const endedTime = formatMeasurementDate(
    coordinateMeasurment["measurementdate"]
  );
  const kmDrivenText = i18next.t("Kilometer_driven");
  const kmDrivenValue = tourData?.kilometerTotal?.toFixed(1).replace(".", ",");

  const msg_start = `
    ${textPopUp} <br/>
    ${textTime}: ${startedTime} <br/>
    ${i18next.t("location")}: ${location} <br/>`;

  const msg_end = `
    ${textPopUp} <br/>
    ${textTime}: ${endedTime} <br/>
    ${i18next.t("location")}: ${location} <br/>
    ${kmDrivenText} : ${kmDrivenValue} km <br/>`;

  const msg = pos === "start" ? msg_start : msg_end;

  marker.bindPopup(msg);
  const markerGroup = L.featureGroup([marker]); // Create a group to hold the marker
  markerGroup.addTo(mapRef.current);
};

/**
 *
 * @param {*} pos
 * @returns
 */
function createIcon(pos) {
  const iconSize = [30, 30];
  const icon = pos === "start" ? iconstart : iconend;
  return L.divIcon({
    className: "custom-icon",
    html: `<img src="${icon}" width= "${iconSize[0]}px" height= "${iconSize[1]}px"/>`,
    iconSize: iconSize,
    iconAnchor: [15, 15],
  });
}

/**
 *
 * @param {*} breaksCoordinates
 * @param {*} mapRef
 */
const addBreaksMarkers = (breaksCoordinates, mapRef) => {
  breaksCoordinates?.forEach((breakCoordinate) => {
    const marker = L.marker(
      [breakCoordinate["latitudeBreak"], breakCoordinate["longitudeBreak"]],
      {
        icon: createBreakIcon(),
        isBreak: true,
      }
    );

    // Add a popup with separate lines and a line connecting the marker and popup
    const startedTime = formatMeasurementDate(breakCoordinate["startedTime"]);
    const endedTime = formatMeasurementDate(breakCoordinate["endedTime"]);

    const totalMinutes = parseInt(breakCoordinate["minutesBreak"]);
    const hours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    const formattedTime = `${hours}:${remainingMinutes
      .toString()
      .padStart(2, "0")}`;

    const msg = `
        ${i18next.t("Stop")} <br/>
        ${i18next.t("Arrival")}: ${startedTime} <br/>
        ${i18next.t("Departure")}: ${endedTime} <br/>
        ${i18next.t("Stay")}: ${formattedTime} (h:m) <br/>
        ${i18next.t("since_last_stop")} ${breakCoordinate[
      "kilometerTotalBeforeBreak"
    ]
      ?.toFixed(1)
      .replace(".", ",")} km <br/>
        `;

    marker.bindPopup(msg);
    const markerGroup = L.featureGroup([marker]); // Create a group to hold the marker
    markerGroup.addTo(mapRef.current);
  });
};

/**
 *
 * @returns
 */
function createBreakIcon() {
  const iconSize = [20, 20];
  const icon = iconpause;
  return L.divIcon({
    className: "custom-icon",
    html: `<img src="${icon}" width= "${iconSize[0]}px" height= "${iconSize[1]}px"/>`,
    iconSize: iconSize,
    iconAnchor: [10, 10],
  });
}

/**
 *
 * @param {*} date
 * @param {*} offsetHours
 * @returns
 */
const convertDateToISOWithOffset = (date) => {
  if (!date) {
    return null;
  }
  const dateWithOffset = new Date(date);
  return dateWithOffset.toISOString();
};

/**
 *
 * @param {*} topSpeedsCoordinates
 * @param {*} mapRef
 */
//TODO: Ask olaf :maybe we can add a polyline
const addTopSpeedsMarkers = (topSpeedsCoordinates, mapRef, polylineGroup) => {
  topSpeedsCoordinates?.forEach((topSpeedCoordinate) => {
    const marker = L.marker(
      [
        topSpeedCoordinate["latitudeSpeedStart"],
        topSpeedCoordinate["longitudeSpeedStart"],
      ],
      {
        icon: createTopSpeedIcon(),
        isTopSpeed: true,
      }
    );
    // Add a popup with separate lines and a line connecting the marker and popup
    const startedTime = formatMeasurementDate(
      topSpeedCoordinate["startedTime"]
    );

    const msg = `
        ${i18next.t("Top_speed")} <br/>
        ${i18next.t("Time")}: ${startedTime} <br/>
        ${i18next.t("Speed")} ${topSpeedCoordinate["topSpeed"]} km/h <br/>
        `;

    marker.bindPopup(msg);
    const markerGroup = L.featureGroup([marker]);
    markerGroup.addTo(mapRef.current);
  });
};

/**
 *
 * @returns
 */
function createTopSpeedIcon() {
  const iconSize = [20, 20];
  const icon = iconTopSpeed;
  return L.divIcon({
    className: "custom-icon",
    html: `<img src="${icon}" width= "${iconSize[0]}px" height= "${iconSize[1]}px"/>`,
    iconSize: iconSize,
    iconAnchor: [10, 10],
  });
}

export {
  geteventdetails,
  getNamespaceList,
  addPolylines,
  addMarkers,
  addBreaksMarkers,
  addTopSpeedsMarkers,
  convertDateToISOWithOffset,
};